import { Check, Printer } from "lucide-react";
import { WordIcon } from "../hero-v2/hero-v2-icons";

type GeneratedLessonPlanCardProps = {
  copy: {
    title: string;
    documentTitle: string;
    meta: readonly {
      label: string;
      value: string;
    }[];
    objectives: readonly string[];
    materials: string;
    sectionLabels: {
      objectives: string;
      materials: string;
    };
    tableHeaders: readonly [string, string, string, string, string];
    stages: readonly {
      stage: string;
      activity: string;
      procedures: readonly string[];
      time: string;
      interaction: string;
    }[];
    exportLabel: string;
    printLabel: string;
  };
  compact?: boolean;
};

export function GeneratedLessonPlanCard({ copy, compact = false }: GeneratedLessonPlanCardProps) {
  const meta = compact ? copy.meta.slice(0, 3) : copy.meta;
  const objectives = compact ? copy.objectives.slice(0, 2) : copy.objectives;
  const stages = compact ? copy.stages.slice(0, 3) : copy.stages;

  return (
    <article className="relative z-10 min-w-0 rounded-[24px] border border-green-200 bg-white p-5 shadow-[0_28px_90px_-45px_rgba(8,166,47,0.75)] ring-1 ring-green-100/80 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-green-300 bg-green-50 text-[#08A62F]">
          <Check className="h-5 w-5" strokeWidth={2.8} aria-hidden />
        </span>
        <h3 className="text-lg font-black text-slate-950">{copy.title}</h3>
      </div>

      <div
        className={`relative mt-4 overflow-y-auto overscroll-contain rounded-[16px] border border-slate-200 bg-white p-4 shadow-inner [scrollbar-gutter:stable] sm:p-5 ${compact ? "max-h-[310px] sm:max-h-[350px] lg:max-h-[370px]" : "max-h-[430px] sm:max-h-[520px] lg:max-h-[560px]"}`}
        aria-label="Generated lesson plan preview"
        tabIndex={0}
      >
        <div className="pr-2">
          <h4 className="text-center text-base font-black leading-tight text-slate-950 sm:text-lg">
            {copy.documentTitle}
          </h4>

          <div className="mt-4 grid grid-cols-1 overflow-hidden rounded-sm border border-slate-200 text-[9px] font-semibold text-slate-800 sm:grid-cols-2">
            {meta.map((item) => (
              <div
                key={`${item.label}-${item.value}`}
                className="border-b border-slate-200 px-2 py-2 last:border-b-0 sm:border-r sm:even:border-r-0 sm:[&:nth-last-child(-n+2)]:border-b-0"
              >
                <span className="font-black text-[#078F2B]">{item.label}: </span>
                <span>{item.value}</span>
              </div>
            ))}
          </div>

          <section className="mt-4">
            <h5 className="text-[11px] font-black tracking-wide text-[#08A62F]">{copy.sectionLabels.objectives}</h5>
            <ul className="mt-2 space-y-1 text-[10px] font-medium leading-snug text-slate-800">
              {objectives.map((objective) => (
                <li key={objective}>• {objective}</li>
              ))}
            </ul>
          </section>

          <section className="mt-4">
            <h5 className="text-[11px] font-black tracking-wide text-[#08A62F]">{copy.sectionLabels.materials}</h5>
            <p className="mt-1.5 text-[10px] font-medium leading-snug text-slate-800">{copy.materials}</p>
          </section>

          {compact ? (
            <div className="mt-4 space-y-2">
              {stages.map((row, index) => (
                <div key={row.stage} className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-[10px] font-black leading-snug text-slate-900">
                      {index + 1}. {row.stage}
                    </p>
                    <span className="shrink-0 text-[9px] font-black text-[#078F2B]">{row.time}</span>
                  </div>
                  <p className="mt-1 text-[9px] font-medium leading-snug text-slate-600">{row.activity}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-sm border border-slate-200">
              <table className="min-w-[560px] w-full table-fixed border-collapse text-left text-[8px] leading-snug text-slate-800">
                <colgroup>
                  <col className="w-[19%]" />
                  <col className="w-[20%]" />
                  <col className="w-[38%]" />
                  <col className="w-[10%]" />
                  <col className="w-[13%]" />
                </colgroup>
                <thead>
                  <tr className="bg-green-100 text-[8px] font-black uppercase text-[#078F2B]">
                    <th className="border-r border-green-200 px-2 py-2 text-center">{copy.tableHeaders[0]}</th>
                    <th className="border-r border-green-200 px-2 py-2 text-center">{copy.tableHeaders[1]}</th>
                    <th className="border-r border-green-200 px-2 py-2 text-center">{copy.tableHeaders[2]}</th>
                    <th className="border-r border-green-200 px-2 py-2 text-center">{copy.tableHeaders[3]}</th>
                    <th className="px-2 py-2 text-center">{copy.tableHeaders[4]}</th>
                  </tr>
                </thead>
                <tbody>
                  {stages.map((row, index) => (
                    <tr key={row.stage} className={index % 2 === 0 ? "bg-white" : "bg-slate-50/70"}>
                      <td className="border-r border-t border-slate-200 px-2 py-2 align-top font-black">
                        {index + 1}. {row.stage}
                      </td>
                      <td className="border-r border-t border-slate-200 px-2 py-2 align-top font-semibold">
                        {row.activity}
                      </td>
                      <td className="border-r border-t border-slate-200 px-2 py-2 align-top">
                        <ul className="space-y-0.5">
                          {row.procedures.map((procedure) => (
                            <li key={procedure}>• {procedure}</li>
                          ))}
                        </ul>
                      </td>
                      <td className="border-t border-slate-200 px-2 py-2 text-center align-top font-semibold">
                        {row.time}
                      </td>
                      <td className="border-l border-t border-slate-200 px-2 py-2 text-center align-top font-semibold">
                        {row.interaction}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          className="inline-flex h-12 items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-950 shadow-sm transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >
          <WordIcon className="h-7 w-7 shrink-0" />
          {copy.exportLabel}
        </button>
        <button
          type="button"
          className="inline-flex h-12 items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-950 shadow-sm transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >
          <Printer className="h-5 w-5 text-[#08A62F]" strokeWidth={2.5} aria-hidden />
          {copy.printLabel}
        </button>
      </div>
    </article>
  );
}
